import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiTrash2, FiEdit, FiCalendar, FiDollarSign, FiTrendingUp, FiPercent, FiTarget, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify'; 
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import api from '../services/api';
import Spinner from '../components/Spinner';
import ConfirmModal from '../components/ConfirmModal';

const expenseCategories = [
  'Loyer',
  'Salaires',
  'Transport',
  'Marketing',
  'Services (eau, électricité)',
  'Autre'
];

ChartJS.register(ArcElement, Tooltip, Legend);

const ExpenseModal = ({ isOpen, onClose, onSave, expense }) => {
  const [formData, setFormData] = useState({ description: '', amount: '', category: 'Autre' });

  useEffect(() => {
    if (expense) {
      setFormData({ description: expense.description, amount: expense.amount, category: expense.category || 'Autre' });
    } else {
      setFormData({ description: '', amount: '', category: 'Autre' });
    }
  }, [expense, isOpen]);

  const handleSave = () => {
    const parsedAmount = parseFloat(formData.amount);
    if (formData.description && !isNaN(parsedAmount) && parsedAmount > 0) {
      onSave({ ...formData, amount: parsedAmount });
      onClose();
    } else {
      toast.error("Veuillez entrer une description et un montant valide.");
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">{expense ? 'Modifier la dépense' : 'Ajouter une dépense'}</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <input name="description" type="text" placeholder="Description (ex: Loyer, Transport...)" value={formData.description} onChange={handleChange} className="w-full p-2 border rounded mb-4" />
        <label className="block text-sm font-medium text-gray-700 mb-1">Montant</label>
        <input name="amount" type="number" placeholder="Montant" value={formData.amount} onChange={handleChange} className="w-full p-2 border rounded mb-4" />
        <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
        <select name="category" value={formData.category} onChange={handleChange} className="w-full p-2 border rounded mb-4 bg-white">
          {expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const BudgetModal = ({ isOpen, onClose, onSave, currentBudget }) => {
  const [amount, setAmount] = useState(currentBudget || '');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-bold mb-4 text-gray-800">Définir le budget mensuel</h3>
        <p className="text-sm text-gray-500 mb-4">Fixez une limite pour vos dépenses ce mois-ci.</p>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full p-3 border rounded-lg mb-6 text-lg font-bold" placeholder="Ex: 200000" autoFocus />
        <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-bold">Annuler</button>
            <button onClick={() => { onSave(amount); onClose(); }} className="px-4 py-2 bg-brand-blue text-white rounded-lg font-bold hover:bg-blue-700">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [periodRevenue, setPeriodRevenue] = useState(0); // Nouvel état pour les revenus
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [budget, setBudget] = useState(() => localStorage.getItem('monthlyBudget') || 0);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);

  const handleSaveBudget = (val) => { setBudget(val); localStorage.setItem('monthlyBudget', val); toast.success("Budget mis à jour !"); };

  const fetchExpenses = async (start, end) => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;

      const response = await api.get('/expenses', { params });
      setExpenses(response.data);

      try {
        // On récupère aussi les revenus pour la même période (peut échouer en plan Essentiel)
        const revenueResponse = await api.get('/reports/summary', { params });
        setPeriodRevenue(revenueResponse.data.revenue || 0);
      } catch (e) {
        setPeriodRevenue(0);
      }
    } catch (error) {
      toast.error("Impossible de charger les dépenses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    fetchExpenses(firstDayOfMonth, lastDayOfMonth);
  }, []);

  const handleSaveExpense = async (expenseData) => {
    if (editingExpense && editingExpense._id) {
      try {
        await api.put(`/expenses/${editingExpense._id}`, expenseData);
        toast.success("Dépense modifiée avec succès !");
        fetchExpenses(startDate, endDate);
      } catch (error) {
        toast.error("Erreur lors de la modification.");
      }
    } else {
      try {
        await api.post('/expenses', expenseData);
        toast.success("Dépense enregistrée avec succès !");
        fetchExpenses(startDate, endDate);
      } catch (error) {
        toast.error("Erreur lors de l'enregistrement.");
      }
    }
    setIsModalOpen(false);
    setEditingExpense(null);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleDeleteRequest = (expense) => {
    setExpenseToDelete(expense);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await api.delete(`/expenses/${expenseToDelete._id}`);
      toast.success("Dépense supprimée.");
      fetchExpenses(startDate, endDate);
    } catch (error) {
      toast.error("Erreur lors de la suppression.");
    } finally {
      setIsConfirmModalOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleFilter = () => {
    // On relance le fetch avec les nouvelles dates.
    // fetchExpenses met à jour la liste des dépenses et le revenu de la période.
    if (!startDate || !endDate) {
      toast.error("Veuillez sélectionner une date de début et de fin.");
      return;
    }
    fetchExpenses(startDate, endDate);
  };

  const setDateRange = (range) => {
    const end = new Date();
    let start = new Date();
    if (range === 'month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (range === '30days') {
      start.setDate(end.getDate() - 30);
    }
    const formattedStart = start.toISOString().split('T')[0];
    const formattedEnd = end.toISOString().split('T')[0];
    setStartDate(formattedStart);
    setEndDate(formattedEnd);
    fetchExpenses(formattedStart, formattedEnd);
  };

  const totalExpenses = useMemo(() => {
    return expenses.reduce((total, exp) => total + exp.amount, 0);
  }, [expenses]);

  const expenseToRevenueRatio = useMemo(() => {
    return periodRevenue > 0 ? (totalExpenses / periodRevenue) * 100 : 0;
  }, [totalExpenses, periodRevenue]);

  const expenseChartData = useMemo(() => {
    const dataByCat = expenses.reduce((acc, exp) => {
      const category = exp.category || 'Autre';
      acc[category] = (acc[category] || 0) + exp.amount;
      return acc;
    }, {});

    return {
      labels: Object.keys(dataByCat),
      datasets: [
        {
          label: 'Dépenses par catégorie',
          data: Object.values(dataByCat),
          backgroundColor: [
            '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', '#10B981'
          ],
          borderColor: '#fff',
          borderWidth: 2,
        },
      ],
    };
  }, [expenses]);

  const chartOptions = {
    responsive: true,
    plugins: { legend: { position: 'right' } }
  };

  const budgetProgress = budget > 0 ? (totalExpenses / budget) * 100 : 0;
  const budgetColor = budgetProgress > 100 ? 'bg-red-500' : budgetProgress > 80 ? 'bg-orange-500' : 'bg-green-500';
  const budgetTextColor = budgetProgress > 100 ? 'text-red-600' : budgetProgress > 80 ? 'text-orange-600' : 'text-green-600';

  if (loading) {
    return <Spinner message="Chargement des dépenses..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Dépenses</h2>
        <button onClick={() => { setEditingExpense(null); setIsModalOpen(true); }} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-colors w-full sm:w-auto">
          <FiPlus className="mr-2" /> Ajouter une dépense
        </button>
      </div>

      {/* Filtres et Résumé */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="md:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="font-semibold mb-2 text-gray-700">Filtrer par période</h3>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-gray-100 border-2 border-gray-200 rounded-lg py-2 px-4 focus:outline-none focus:border-brand-blue w-full"/>
            <span className="hidden md:inline text-center">-</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-gray-100 border-2 border-gray-200 rounded-lg py-2 px-4 focus:outline-none focus:border-brand-blue w-full"/>
            <button onClick={handleFilter} className="bg-brand-blue text-white font-bold py-2 px-4 rounded-lg w-full md:w-auto">Filtrer</button>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setDateRange('month')} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 py-1 px-3 rounded-full">Ce mois-ci</button>
            <button onClick={() => setDateRange('30days')} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 py-1 px-3 rounded-full">30 derniers jours</button>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
          <div className="flex justify-between items-start z-10">
             <div><p className="text-sm text-gray-500 font-bold uppercase">Budget Mensuel</p><p className="text-2xl font-extrabold text-gray-800">{parseInt(budget).toLocaleString()} F</p></div>
             <button onClick={() => setIsBudgetModalOpen(true)} className="text-gray-400 hover:text-brand-blue"><FiEdit/></button>
          </div>
          <div className="mt-4 z-10">
             <div className="flex justify-between text-xs font-bold mb-1"><span className={budgetTextColor}>{budgetProgress.toFixed(0)}% utilisé</span><span>{totalExpenses.toLocaleString()} F</span></div>
             <div className="w-full bg-gray-100 rounded-full h-2"><div className={`h-2 rounded-full transition-all duration-500 ${budgetColor}`} style={{width: `${Math.min(budgetProgress, 100)}%`}}></div></div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="bg-red-50 p-4 rounded-xl border border-red-100 flex items-center justify-between">
              <div><p className="text-xs text-red-500 font-bold uppercase">Total Dépenses</p><p className="text-xl font-bold text-red-700">{totalExpenses.toLocaleString('fr-FR')} F</p></div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100 flex items-center justify-between">
              <div><p className="text-xs text-yellow-600 font-bold uppercase">Ratio / Revenus</p><p className="text-xl font-bold text-yellow-700">{expenseToRevenueRatio.toFixed(1)}%</p></div>
          </div>
        </div>
      </div>

      {expenses.length > 0 && (
        <div className="mb-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Répartition des Dépenses</h3>
          <div className="h-64 flex items-center justify-center">
            <Doughnut data={expenseChartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Vue Mobile (Cartes) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {expenses.map((expense) => (
          <div key={expense._id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-all duration-200">
            <div>
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500">{new Date(expense.date).toLocaleDateString('fr-FR')}</span>
                <span className="bg-gray-200 text-gray-700 py-1 px-2 rounded-full text-xs">{expense.category || 'N/A'}</span>
              </div>
              <h3 className="font-bold text-gray-800 mb-1">{expense.description}</h3>
              <p className="text-xl font-bold text-red-600 mb-3">{expense.amount.toLocaleString('fr-FR')} CFA</p>
            </div>
            <div className="flex justify-end space-x-3 border-t pt-3 mt-3">
              <button onClick={() => handleEdit(expense)} className="text-gray-500 hover:text-brand-blue p-2 bg-gray-100 rounded-full" title="Modifier">
                <FiEdit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDeleteRequest(expense)} className="text-gray-500 hover:text-red-500 p-2 bg-gray-100 rounded-full" title="Supprimer">
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
          <thead className="sticky top-0 z-10">
            <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Description</th>
              <th className="px-5 py-3">Catégorie</th>
              <th className="px-5 py-3 text-right">Montant</th>
              <th className="px-5 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length > 0 ? expenses.map(expense => (
              <tr key={expense._id} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="px-5 py-4 text-sm">{new Date(expense.date).toLocaleDateString('fr-FR')}</td>
                <td className="px-5 py-4 text-sm">
                  <p className="text-gray-900 whitespace-no-wrap font-semibold">{expense.description}</p>
                </td>
                <td className="px-5 py-4 text-sm">
                  <span className="bg-gray-200 text-gray-700 py-1 px-2 rounded-full text-xs">{expense.category || 'N/A'}</span>
                </td>
                <td className="px-5 py-4 text-sm text-right font-semibold text-red-600">{expense.amount.toLocaleString('fr-FR')} CFA</td>
                <td className="px-5 py-4 text-sm text-center">
                  <div className="flex item-center justify-center space-x-3">
                    <button onClick={() => handleEdit(expense)} className="text-gray-500 hover:text-brand-blue" title="Modifier"><FiEdit className="w-5 h-5" /></button>
                    <button onClick={() => handleDeleteRequest(expense)} className="text-gray-500 hover:text-red-500" title="Supprimer"><FiTrash2 className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" className="text-center py-10 text-gray-500">Aucune dépense pour la période sélectionnée.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      <ExpenseModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingExpense(null); }} 
        onSave={handleSaveExpense}
        expense={editingExpense}
      />
      <BudgetModal isOpen={isBudgetModalOpen} onClose={() => setIsBudgetModalOpen(false)} onSave={handleSaveBudget} currentBudget={budget} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer la dépense"
        message={`Êtes-vous sûr de vouloir supprimer la dépense "${expenseToDelete?.description}" ? Cette action est irréversible.`}
      />
    </div>
  );
};

export default Expenses;